/**
 * Leitura da carteira para a lista de Clientes: cada cliente vira uma linha
 * com saúde, prazo, etapa, risco e (quando a pessoa vê a Central) quem espera
 * e pendências; e a carteira inteira vira indicadores, saúde, próximas
 * entregas e a Leitura do Analista. Funções puras.
 */
import { normalizeText } from "../../../../utils/data";
import { toDate } from "../../../../utils/status";
import { SITUACOES, situacaoDoCliente, compararTexto } from "./montarClientes";
import {
  ESPERA,
  FAIXAS,
  ROTULO_DA_FAIXA,
  acaoSugerida,
  diasAte,
  donoDaAcao,
  entregaEmBreve,
  entregaVencida,
  esperaMaisUrgente,
  estaAtrasado,
  etapaDoCliente,
  faixaDeSaude,
  pontosDeRisco,
  prazoRelativo,
  tomDoPrazo,
  DIAS_DE_ENTREGA_PROXIMA,
} from "./regras";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const PONTOS_DA_SERIE = 8;
const SUGESTOES = 3;
const PROXIMAS_ENTREGAS = 4;
const JANELA_PROXIMAS = { de: -7, ate: 14 };

/** dd/mm */
export function diaMes(data) {
  const d = toDate(data);
  return d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}` : "";
}

/** Dados da Central somados entre os pedidos do cliente; null se a pessoa não vê a Central. */
function centralDoCliente(cliente, central) {
  if (!central) return null;
  const registros = cliente.numerosDosPedidos.map((n) => central[n]).filter(Boolean);
  return {
    espera: esperaMaisUrgente(registros.map((r) => r.quem_espera)),
    pendencias: registros.reduce((soma, r) => soma + (r.pendencias_abertas || 0), 0),
    projetistas: [...new Set(registros.flatMap((r) => r.projetistas || []))].sort(compararTexto),
    urgencia: registros.reduce((max, r) => Math.max(max, r.urgencia ?? 0), 0) || null,
  };
}

export function montarLinha(cliente, central, hoje) {
  const dias = diasAte(cliente.proximaEntrega, hoje);
  const atrasoAcumulado = cliente.totalDelayDays;
  const atrasado = estaAtrasado({ diasProximaEntrega: dias, atrasoAcumulado });
  const daCentral = centralDoCliente(cliente, central);
  const espera = daCentral?.espera ?? null;
  const pendencias = daCentral?.pendencias ?? 0;
  const proximaEntregaLabel = diaMes(cliente.proximaEntrega);
  return {
    id: cliente.id,
    nome: cliente.cliente,
    pedidos: cliente.numerosDosPedidos,
    totalPedidos: cliente.totalPedidos,
    totalAmbientes: cliente.totalAmbientes,
    entregues: cliente.totalEntregues,
    emAndamento: cliente.totalEmAndamento,
    saude: cliente.healthPercentage,
    faixa: faixaDeSaude({ saude: cliente.healthPercentage, atrasado, atrasoAcumulado }),
    atrasoAcumulado,
    atrasado,
    proximaEntrega: cliente.proximaEntrega,
    diasProximaEntrega: dias,
    proximaEntregaLabel,
    prazo: prazoRelativo(dias),
    tomPrazo: tomDoPrazo(dias, { atrasoAcumulado }),
    etapa: etapaDoCliente(cliente.pedidos.flatMap((p) => p.ambientes)),
    espera,
    pendencias,
    projetista: daCentral?.projetistas[0] || "",
    valor: cliente.valor,
    situacao: situacaoDoCliente(cliente),
    risco: pontosDeRisco({ saude: cliente.healthPercentage, atrasoAcumulado, diasProximaEntrega: dias, pendencias, espera }),
    acao: acaoSugerida({ diasProximaEntrega: dias, atrasoAcumulado, pendencias, espera, proximaEntregaLabel }),
    busca: normalizeText(
      [cliente.cliente, ...cliente.numerosDosPedidos, ...cliente.vendedores, ...cliente.consultores, ...cliente.lotes,
        ...(daCentral?.projetistas || [])].join(" "),
    ).toLowerCase(),
    cliente,
  };
}

// ------------------------------------------------------------
// Séries reais para os sparklines (sem tendência inventada)
// ------------------------------------------------------------

function inicioDaSemana(data) {
  const d = new Date(data);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

/** Ambientes entregues (carregamento) por semana, nas últimas 8 semanas. */
export function serieDeEntregasPorSemana(clientes, hoje) {
  const fim = inicioDaSemana(hoje);
  const serie = Array(PONTOS_DA_SERIE).fill(0);
  clientes.forEach((c) => c.pedidos.forEach((p) => p.ambientes.forEach((a) => {
    const d = toDate(a.actualDeliveryDate);
    if (!d) return;
    const semanas = Math.round((fim - inicioDaSemana(d)) / (7 * 864e5));
    if (semanas >= 0 && semanas < PONTOS_DA_SERIE) serie[PONTOS_DA_SERIE - 1 - semanas] += 1;
  })));
  return serie;
}

function indiceDoMes(data, hoje) {
  return (hoje.getFullYear() - data.getFullYear()) * 12 + (hoje.getMonth() - data.getMonth());
}

/** Pedidos vendidos e valor vendido por mês, nos últimos 8 meses. */
export function seriesDeVendasPorMes(clientes, hoje) {
  const pedidos = Array(PONTOS_DA_SERIE).fill(0);
  const valor = Array(PONTOS_DA_SERIE).fill(0);
  clientes.forEach((c) => c.pedidos.forEach((p) => {
    const d = toDate(p.dataVenda);
    if (!d) return;
    const meses = indiceDoMes(d, hoje);
    if (meses < 0 || meses >= PONTOS_DA_SERIE) return;
    pedidos[PONTOS_DA_SERIE - 1 - meses] += 1;
    valor[PONTOS_DA_SERIE - 1 - meses] += p.valorTotal;
  }));
  return { pedidos, valor };
}

function plural(n, um, varios) {
  return `${n} ${n === 1 ? um : varios}`;
}

function montarIndicadores(clientes, linhas, hoje) {
  const ativas = linhas.filter((l) => l.situacao === SITUACOES.EM_ANDAMENTO);
  const concluidos = linhas.filter((l) => l.situacao === SITUACOES.CONCLUIDOS).length;
  const pedidosAtivos = clientes.flatMap((c) => c.pedidos).filter((p) => p.totalEmAndamento > 0);
  const ambientesEmAndamento = pedidosAtivos.reduce((soma, p) => soma + p.totalEmAndamento, 0);
  const limiteDaJanela = new Date(hoje);
  limiteDaJanela.setDate(limiteDaJanela.getDate() + DIAS_DE_ENTREGA_PROXIMA);
  const vendas = seriesDeVendasPorMes(clientes, hoje);
  return [
    { id: "ativos", rotulo: "Clientes ativos", valor: ativas.length, cor: "accent",
      legenda: `${linhas.length} no total · ${concluidos} concluídos` },
    { id: "atraso", rotulo: "Em atraso", valor: ativas.filter((l) => l.atrasado).length, cor: "bad", alerta: true,
      legenda: "entrega vencida ou atraso acumulado" },
    { id: "entregas", rotulo: "Entregas em 7 dias", valor: ativas.filter((l) => entregaEmBreve(l.diasProximaEntrega)).length,
      cor: "warn", legenda: `até ${diaMes(limiteDaJanela)}`,
      serie: serieDeEntregasPorSemana(clientes, hoje), serieRotulo: "Ambientes entregues por semana, últimas 8 semanas" },
    { id: "pedidos", rotulo: "Pedidos ativos", valor: pedidosAtivos.length, cor: "c2",
      legenda: plural(ambientesEmAndamento, "ambiente em andamento", "ambientes em andamento"),
      serie: vendas.pedidos, serieRotulo: "Pedidos vendidos por mês, últimos 8 meses" },
    { id: "carteira", rotulo: "Valor em carteira", valor: clientes.reduce((s, c) => s + c.valorEmCarteira, 0), cor: "ok",
      dinheiro: true, legenda: "ambientes ainda não entregues",
      serie: vendas.valor, serieRotulo: "Valor vendido por mês, últimos 8 meses" },
  ];
}

function montarSaude(ativas) {
  const total = ativas.length;
  const fatias = [FAIXAS.SAUDAVEL, FAIXAS.ATENCAO, FAIXAS.CRITICO].map((faixa) => ({
    faixa, rotulo: ROTULO_DA_FAIXA[faixa], total: ativas.filter((l) => l.faixa === faixa).length,
  }));
  // Sem cliente ativo não há média: null (a tela mostra "—"), nunca "100%".
  const media = total ? Math.round(ativas.reduce((s, l) => s + l.saude, 0) / total) : null;
  return { total, media, fatias };
}

function montarEsperas(ativas) {
  const total = ativas.length;
  return [
    { espera: ESPERA.EQUIPE, rotulo: "Cliente espera a equipe", cor: "bad" },
    { espera: ESPERA.CLIENTE, rotulo: "A equipe espera o cliente", cor: "warn" },
    { espera: ESPERA.NINGUEM, rotulo: "Ninguém esperando", cor: "ok" },
  ].map((e) => {
    const n = ativas.filter((l) => l.espera === e.espera).length;
    return { ...e, total: n, proporcao: total ? n / total : 0 };
  });
}

function montarProximasEntregas(ativas) {
  return ativas
    .filter((l) => l.diasProximaEntrega != null
      && l.diasProximaEntrega >= JANELA_PROXIMAS.de && l.diasProximaEntrega <= JANELA_PROXIMAS.ate)
    .sort((a, b) => a.diasProximaEntrega - b.diasProximaEntrega || compararTexto(a.nome, b.nome))
    .slice(0, PROXIMAS_ENTREGAS)
    .map((l) => {
      const d = toDate(l.proximaEntrega);
      return {
        id: l.id, nome: l.nome, dia: String(d.getDate()).padStart(2, "0"), mes: MESES[d.getMonth()],
        detalhe: `${l.emAndamento} em andamento · ${l.etapa.rotulo.toLowerCase()}`,
        prazo: l.prazo, tom: tomDoPrazo(l.diasProximaEntrega),
      };
    });
}

/** Nome curto para a lista de ações: duas palavras, três se a segunda for "E". */
function nomeCurto(nome) {
  const partes = String(nome).split(/\s+/);
  return partes.slice(0, partes[1] === "E" ? 3 : 2).join(" ");
}

function montarLeitura(ativas, comCentral) {
  const atrasados = ativas.filter((l) => l.atrasado);
  const pior = [...ativas].sort((a, b) => b.atrasoAcumulado - a.atrasoAcumulado)[0];
  const sugestoes = [...ativas]
    .sort((a, b) => b.risco - a.risco)
    .slice(0, SUGESTOES)
    .map((l, i) => ({ id: l.id, ordem: String(i + 1).padStart(2, "0"), nome: nomeCurto(l.nome), acao: l.acao,
      dono: donoDaAcao(l.espera) }));
  return {
    atrasados: atrasados.length,
    entregasProximas: ativas.filter((l) => entregaEmBreve(l.diasProximaEntrega)).length,
    vencidas: ativas.filter((l) => entregaVencida(l.diasProximaEntrega)).length,
    pior: pior && pior.atrasoAcumulado > 0 ? { id: pior.id, nome: pior.nome, dias: pior.atrasoAcumulado,
      esperaEquipe: pior.espera === ESPERA.EQUIPE } : null,
    esperamEquipe: comCentral ? ativas.filter((l) => l.espera === ESPERA.EQUIPE).length : null,
    sugestoes,
  };
}

/**
 * `central`: mapa número do pedido → dados da Central (null quando a pessoa
 * não tem `central_projetos:view` ou a Central está desligada).
 */
export function analisarCarteira(clientes, central = null, hoje = new Date()) {
  const linhas = clientes.map((c) => montarLinha(c, central, hoje));
  const ativas = linhas.filter((l) => l.situacao === SITUACOES.EM_ANDAMENTO);
  const porSituacao = {
    [SITUACOES.TODOS]: linhas.length,
    [SITUACOES.PENDENTES]: linhas.filter((l) => l.situacao === SITUACOES.PENDENTES).length,
    [SITUACOES.EM_ANDAMENTO]: ativas.length,
    [SITUACOES.CONCLUIDOS]: linhas.filter((l) => l.situacao === SITUACOES.CONCLUIDOS).length,
  };
  return {
    linhas,
    porSituacao,
    indicadores: montarIndicadores(clientes, linhas, hoje),
    saude: montarSaude(ativas),
    esperas: central ? montarEsperas(ativas) : null,
    proximasEntregas: montarProximasEntregas(ativas),
    leitura: montarLeitura(ativas, Boolean(central)),
  };
}
